<template>
    <div class="flex mt-6 justify-between">
        <h1 class="text-2xl font-bold text-gray-800">Alerts</h1>
    </div>
    <div>
        <BasicTabs :tabs="tabs" :tab-key="'settings'" />
    </div>

    <!-- Upgrade Required Section -->
    <UpgradeRequired v-if="!hasAlertsAccess" />

    <!-- Main Content -->
    <div v-else class="mb-[98px]">
        <div class="grid-cols-layout border-b border-gray-300 pb-5 lg:grid">
            <div class="mb-6 lg:mb-0 lg:mt-4">
                <h3
                    class="gray-800 mb-[5px] text-lg font-medium leading-normal tracking-small"
                >
                    Alert Policy
                </h3>
                <p class="max-w-xs text-sm text-gray-500">
                    Set the compliance drop threshold and define the time frame
                    over which it should be monitored to trigger an alert.
                </p>
            </div>

            <div class="mx-auto w-full rounded-md bg-white shadow-card lg:mx-0">
                <!-- Alert Status Section -->
                <div v-if="doesNotExist || isDisabled" class="p-6">
                    <div class="flex items-center justify-between">
                        <div>
                            <h4 class="text-lg font-medium text-gray-900">
                                {{
                                    doesNotExist
                                        ? "Alerts Not Configured"
                                        : "Alerts Disabled"
                                }}
                            </h4>
                            <p class="text-sm text-gray-600 mt-1">
                                {{
                                    doesNotExist
                                        ? "You haven't set up alerts yet. Enable alerts to get notified when compliance drops."
                                        : "Your alerts are currently disabled. Enable them to start receiving notifications."
                                }}
                            </p>
                        </div>
                        <AtomicButton
                            type="button"
                            color="green"
                            size="sm"
                            :disabled="enableLoading"
                            @click="enableAlerts"
                        >
                            {{
                                enableLoading ? "Enabling..." : "Enable Alerts"
                            }}
                        </AtomicButton>
                    </div>
                </div>

                <div v-if="isEnabled" class="p-6">
                    <div class="space-y-6">
                        <!-- Compliance Drop Input -->
                        <div>
                            <label
                                class="block text-sm font-medium text-gray-700 mb-2"
                            >
                                Compliance Drop
                            </label>
                            <div class="flex items-center space-x-3">
                                <NumberInput
                                    v-model="complianceDropNumber"
                                    :min="1"
                                    :max="100"
                                />
                                <span class="text-sm text-gray-500">%</span>
                            </div>
                            <p class="mt-1 text-xs text-gray-500">
                                Set the percentage drop in compliance that will
                                trigger an alert (1-100%)
                            </p>
                        </div>

                        <!-- Time Frame Select -->
                        <div>
                            <div class="w-48">
                                <SelectInput
                                    v-model="timeFrame"
                                    label="Time Frame"
                                    placeholder="Select time frame"
                                    :data="timeFrameOptions"
                                >
                                    <template #help>
                                        Define the time period over which the
                                        compliance drop should be monitored
                                    </template>
                                </SelectInput>
                            </div>
                        </div>
                    </div>
                </div>

                <div
                    v-if="isEnabled"
                    class="flex justify-between border-t border-gray-200 mt-6"
                >
                    <div class="my-3 pl-6">
                        <AtomicButton
                            type="button"
                            color="red"
                            size="sm"
                            :disabled="disableLoading"
                            @click="disableAlerts"
                        >
                            {{
                                disableLoading
                                    ? "Disabling..."
                                    : "Disable Alerts"
                            }}
                        </AtomicButton>
                    </div>
                    <div class="my-3 pr-6">
                        <AtomicButton
                            type="button"
                            color="yellow"
                            size="sm"
                            :disabled="!hasChanges || loading"
                            @click="saveSettings"
                        >
                            {{ loading ? "Saving..." : "Save Changes" }}
                        </AtomicButton>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import BasicTabs from "@/components/Atomic/Tabs/BasicTabs.vue";
import NumberInput from "@/components/Atomic/Inputs/NumberInput.vue";
import SelectInput from "@/components/Atomic/Inputs/SelectInput.vue";
import AtomicButton from "@/components/Atomic/Buttons/AtomicButton.vue";
import UpgradeRequired from "@/components/Atomic/Miscellaneous/UpgradeRequired.vue";
import useAlertsAccess from "@/composables/useAlertsAccess";
import { Bell, Settings } from "lucide-vue-next";
import { ref, computed, useSlots } from "vue";
import { router } from "@inertiajs/vue3";
import route from "ziggy-js";
import { create } from "@/composables/useNotifications";

import type TabItem from "@/types/Settings/TabItem";
import type ISelectable from "@/types/Atomic/ISelectable";

const { hasAlertsAccess } = useAlertsAccess();

// Props from backend
interface Props {
    alert?: {
        id?: number;
        compliance_drop?: number;
        time_frame?: number;
        enabled?: boolean;
    };
}

const props = defineProps<Props>();

// Time frame options (values in seconds)
const timeFrameOptions: ISelectable[] = [
    { value: 86400, title: "24 hours" }, // 24 * 60 * 60 = 86400 seconds
    { value: 172800, title: "48 hours" }, // 48 * 60 * 60 = 172800 seconds
    { value: 604800, title: "7 days" }, // 7 * 24 * 60 * 60 = 604800 seconds
];

// Form data
const complianceDropNumber = ref<number>(props.alert?.compliance_drop || 25);
const timeFrame = ref<number>(props.alert?.time_frame || 86400);

// Loading state
const loading = ref<boolean>(false);
const enableLoading = ref<boolean>(false);
const disableLoading = ref<boolean>(false);

// Check if alert exists
const alertExists = computed(() => !!props.alert?.id);

// Check if alert is enabled (use props directly)
const isEnabled = computed(
    () => alertExists.value && props.alert?.enabled === true,
);

// Check if alert is disabled
const isDisabled = computed(
    () => alertExists.value && props.alert?.enabled === false,
);

// Check if alert doesn't exist
const doesNotExist = computed(() => !alertExists.value);

// Original values to track changes
const originalComplianceDrop = ref<number>(props.alert?.compliance_drop || 25);
const originalTimeFrame = ref<number>(props.alert?.time_frame || 86400);

// Check if form has changes
const hasChanges = computed(() => {
    return (
        complianceDropNumber.value !== originalComplianceDrop.value ||
        timeFrame.value !== originalTimeFrame.value
    );
});

// Enable alerts method
const enableAlerts = async () => {
    if (enableLoading.value) return;

    enableLoading.value = true;

    try {
        await router.post(route("alerts.enabled.store"));

        // Show success notification
        create({
            title: "Alerts Enabled",
            text: "Alerts have been successfully enabled and configured.",
            type: "success",
        });
    } catch (error) {
        console.error("Error enabling alerts:", error);
    } finally {
        enableLoading.value = false;
    }
};

// Disable alerts method
const disableAlerts = async () => {
    if (disableLoading.value) return;

    disableLoading.value = true;

    try {
        await router.post(
            route("alerts.disabled.store"),
            {},
            {
                preserveScroll: true,
                onSuccess: () => {
                    create({
                        title: "Alerts Disabled",
                        text: "Alerts have been successfully disabled.",
                        type: "success",
                    });
                },
                onError: () => {
                    // Error will be handled by the backend flash message
                },
                onFinish: () => {
                    disableLoading.value = false;
                },
            },
        );
    } catch (error) {
        console.error("Error disabling alerts:", error);
        disableLoading.value = false;
    }
};

// Save settings method
const saveSettings = async () => {
    if (!hasChanges.value || loading.value) return;

    loading.value = true;

    try {
        await router.put(
            route("alerts.settings.update"),
            {
                compliance_drop: complianceDropNumber.value,
                time_frame: timeFrame.value,
                enabled: true,
            },
            {
                preserveScroll: true,
                onSuccess: () => {
                    create({
                        title: "Alerts settings saved successfully",
                        text: "Alerts have been successfully saved.",
                        type: "success",
                    });
                },
            },
        );

        // Update original values after successful save
        originalComplianceDrop.value = complianceDropNumber.value;
        originalTimeFrame.value = timeFrame.value;
    } catch (error) {
        console.error("Error saving alert settings:", error);
    } finally {
        loading.value = false;
    }
};

const tabs: TabItem[] = [
    {
        title: "Overview",
        key: "alerts/overview",
        icon: Bell,
        url: "/alerts/overview",
    },
    {
        title: "Settings",
        key: "settings",
        icon: Settings,
        url: "/alerts/settings",
    },
];
</script>
